import { callServiceAPI } from '@/lib/firebase/functions';
import { openWindowSafe } from '@/lib/utils/popup';
import { auth } from '@/config/firebase';

interface IssueSsoResult {
  status?: 'success' | 'error';
  token?: string;
  error?: string;
}

async function mintSsoToken(returnOrigin: string): Promise<string | null> {
  try {
    const result = await callServiceAPI({
      appCode: 'sso',
      url: 'issue_sso_token',
      method: 'post',
      data: { returnOrigin },
    });
    const payload = result.data as IssueSsoResult;
    if (payload?.status === 'success' && payload.token) return payload.token;
    console.warn('[sso] issue_sso_token returned', payload);
    return null;
  } catch (err) {
    console.warn('[sso] issue_sso_token call failed', err);
    return null;
  }
}

/**
 * Open an OAuth authorize URL in a popup, pre-authenticating the popup so that
 * Firebase auth state is available even when the caller is inside a partitioned
 * iframe (Chrome storage partitioning, Safari ITP, etc.).
 *
 * Flow: mint a one-hour Firebase custom token for the current user, open the
 * popup to `/oauth-home#_ua_sso_token=...&_ua_sso_dest=<oauthUrl>`, and the
 * callback page signs in with the token before redirecting to the OAuth
 * provider. The token is in the URL fragment (hash), so it does not reach
 * server logs or the referer header.
 *
 * If token minting fails, falls back to opening the popup directly — non-iframe
 * users still have their auth state via same-origin IndexedDB.
 */
export async function openAuthPopup(
  oauthUrl: string,
  features: string = 'popup,width=600,height=700',
): Promise<Window | null> {
  const expectedUid = auth.currentUser?.uid ?? '';
  const token = await mintSsoToken(window.location.origin);
  // Fall back to a plain popup if token minting is unavailable. Still pass the
  // expected UID so OAuthCallback can refuse if the popup's cached session
  // belongs to a different user.
  const hashParams = new URLSearchParams({ _ua_sso_dest: oauthUrl });
  if (expectedUid) hashParams.set('_ua_sso_uid', expectedUid);
  if (token) hashParams.set('_ua_sso_token', token);
  const prepUrl = `${window.location.origin}/login-ensure#${hashParams.toString()}`;
  return openWindowSafe(prepUrl, { target: 'Authorize', features });
}

